import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { LOGO } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = {
  new: "bg-[#C4A484]/20 text-[#8a6d4b] border-[#C4A484]",
  contacted: "bg-[#0D2A27]/10 text-[#0D2A27] border-[#0D2A27]/40",
  closed: "bg-[#111]/5 text-[#555] border-[#555]/30",
};

export default function Admin() {
  const [pass, setPass] = useState("");
  const [authed, setAuthed] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async (passcode) => {
    const res = await axios.get(`${API}/admin/inquiries`, { headers: { "X-Admin-Passcode": passcode } });
    setInquiries(res.data);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await load(pass);
      setAuthed(true);
    } catch (err) {
      setError("Invalid passcode. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    setInquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, { headers: { "X-Admin-Passcode": pass } });
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error("Could not update status");
      load(pass);
    }
  };

  const counts = useMemo(() => {
    const c = { all: inquiries.length, new: 0, contacted: 0, closed: 0 };
    inquiries.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [inquiries]);

  const visible = filter === "all" ? inquiries : inquiries.filter((q) => q.status === filter);

  const exportCsv = () => {
    const cols = ["Name", "Email", "Phone", "Treatment", "Status", "Received", "Message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = visible.map((q) => [q.name, q.email, q.phone, q.service, q.status, new Date(q.created_at).toLocaleString(), q.message].map(esc).join(","));
    const csv = [cols.join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uheal-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D2A27] px-6">
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <img src={LOGO} alt="U-Heal" className="h-14 mx-auto mb-8 bg-[#F7F6F2] rounded-md p-1" />
          <h1 className="font-serif text-3xl text-[#F7F6F2] text-center mb-8">Admin Access</h1>
          <input type="password" data-testid="admin-passcode" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Enter passcode" className="w-full bg-transparent border-b border-[#F7F6F2]/30 py-3 text-[#F7F6F2] placeholder-[#F7F6F2]/40 outline-none focus:border-[#C4A484] transition-colors" />
          {error && <p className="mt-3 text-sm text-red-300" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-8 w-full rounded-full bg-[#C4A484] py-3 text-sm font-semibold text-[#0D2A27] transition-colors hover:bg-[#F7F6F2] disabled:opacity-60">
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-16 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-[#C4A484] mb-3">Inbox</p>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0D2A27]">Patient Inquiries</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#555]" data-testid="admin-count">{counts.all} total</span>
            <button onClick={exportCsv} disabled={visible.length === 0} data-testid="admin-export-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0D2A27] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#F7F6F2] transition-colors hover:bg-[#163B37] disabled:opacity-40">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${filter === s ? "bg-[#0D2A27] text-[#F7F6F2] border-[#0D2A27]" : "bg-transparent text-[#0D2A27] border-[#0D2A27]/25 hover:bg-[#0D2A27]/5"}`}>
              {s} ({counts[s] || 0})
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="text-[#555]" data-testid="admin-empty">No inquiries in this view.</p>
        ) : (
          <div className="space-y-4" data-testid="admin-inquiries">
            {visible.map((q) => (
              <div key={q.id} className="bg-[#FFFFFF] border border-[#E2E0D8] p-6" data-testid="admin-inquiry-row">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-[#0D2A27]">{q.name}</span>
                    <span className="text-xs uppercase tracking-wider bg-[#EDECE6] text-[#0D2A27] px-3 py-1 rounded-full">{q.service}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_STYLE[q.status] || STATUS_STYLE.new}`} data-testid={`status-badge-${q.id}`}>{q.status}</span>
                  </div>
                  <span className="text-xs text-[#555]">{new Date(q.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 text-sm text-[#555] flex flex-wrap gap-x-6">
                  <span>{q.email}</span>
                  {q.phone && <span>{q.phone}</span>}
                </div>
                <p className="mt-4 text-[#111] leading-relaxed">{q.message}</p>

                <div className="mt-5 flex items-center gap-2 border-t border-[#E2E0D8] pt-4">
                  <span className="text-xs text-[#555] mr-1">Mark as:</span>
                  <div className="flex rounded-full border border-[#0D2A27]/20 overflow-hidden">
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => setStatus(q.id, s)} data-testid={`status-btn-${s}-${q.id}`} className={`px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${q.status === s ? "bg-[#0D2A27] text-[#F7F6F2]" : "text-[#0D2A27] hover:bg-[#0D2A27]/5"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
